import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FileText, MessageSquare, Mic, Layers, Shield, ArrowRight,
  Check, Brain, Sparkles, Lock, Eye, Cpu, Database, Globe, Volume2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useContentValue } from "../context/ContentContext";
import Footer from "../components/Footer";

export default function Features() {
  const { t } = useTranslation();
  const eyebrow = useContentValue("features.hero.eyebrow", "[ todas las funciones ]");
  const title = useContentValue("features.hero.title", "Una plataforma. Todo lo que necesitás.");
  const sub = useContentValue("features.hero.subtitle", "");

  const sections = [
    {k: "docs", accent: "from-primary/20", Visual: DocsVisual},
    {k: "chat", accent: "from-violet-500/20", Visual: ChatVisual},
    {k: "voice", accent: "from-emerald-500/20", Visual: VoiceVisual},
    {k: "workspace", accent: "from-amber-500/20", Visual: WorkspaceVisual},
    {k: "security", accent: "from-rose-500/20", Visual: SecurityVisual},
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="features-page">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/"><BrandLogo size="lg" /></Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/features" className="text-foreground font-semibold">{t("nav.features")}</Link>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground font-medium">{t("nav.pricing")}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/signup"><Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">{t("cta.get_started")}</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border" data-testid="features-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-primary font-bold">{eyebrow}</div>
          <h1 className="mt-4 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-tight">{title}</h1>
          <p className="mt-5 text-lg text-muted-foreground max-w-2xl mx-auto">{sub}</p>
        </div>
      </section>

      {/* Deep-dive sections (alternating) */}
      {sections.map((s, i) => (
        <FeatureSection key={s.k} {...s} idx={i} reverse={i % 2 === 1} />
      ))}

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 text-center">
          <h2 className="font-display font-black tracking-tight text-3xl lg:text-4xl">{useContentValue("features.cta.title", "¿Listo para empezar?")}</h2>
          <p className="mt-3 text-primary-foreground/80">{useContentValue("features.cta.subtitle", "Tus primeros 100 tokens están esperando.")}</p>
          <div className="mt-6">
            <Link to="/signup">
              <Button size="lg" variant="secondary" className="rounded-sm h-12 px-8 bg-white text-primary hover:bg-white/90" data-testid="features-cta-btn">
                {t("cta.get_started")} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FeatureSection({ k, accent, idx, reverse, Visual }) {
  const eyebrow = useContentValue(`features.${k}.eyebrow`, k);
  const title = useContentValue(`features.${k}.title`, "");
  const body = useContentValue(`features.${k}.body`, "");
  const bullets = useContentValue(`features.${k}.bullets`, "").split("·").map(s => s.trim()).filter(Boolean);

  return (
    <section className={`border-b border-border ${idx % 2 === 0 ? "bg-background" : "bg-muted/20"}`} data-testid={`feature-section-${k}`}>
      <div className={`max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <motion.div initial={{opacity:0, x: reverse ? 30 : -30}} whileInView={{opacity:1, x:0}} viewport={{once:true}} transition={{duration:0.6}}>
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-primary font-bold">[ {eyebrow} ]</div>
          <h2 className="mt-3 font-display font-black tracking-tight text-3xl lg:text-4xl leading-tight">{title}</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{body}</p>
          <ul className="mt-6 space-y-2.5">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{opacity:0, x: reverse ? -30 : 30}} whileInView={{opacity:1, x:0}}
          viewport={{once:true}} transition={{duration:0.6}}
          className={`relative w-full aspect-square max-w-md mx-auto rounded-md bg-gradient-to-br ${accent} via-background to-background border border-border overflow-hidden`}
        >
          <div className="absolute inset-0 grid-bg opacity-[0.35]" />
          <Visual />
        </motion.div>
      </div>
    </section>
  );
}

// ---------- Visuals ---------- //

function DocsVisual() {
  // Stack of three docs, source (PDF), markdown output and chunks list.
  return (
    <div className="absolute inset-0 p-6 grid place-items-center" data-testid="docs-visual">
      <div className="relative w-full h-full">
        {/* Source PDF */}
        <motion.div
          initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:0.5, delay:0.05}}
          className="absolute top-4 left-4 w-44 bg-card border border-border rounded-sm shadow-xl p-3.5 rotate-[-4deg]"
        >
          <div className="flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-rose-500" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">PDF</span>
          </div>
          <div className="mt-2 space-y-1.5">
            <div className="h-1.5 bg-muted rounded-sm w-full" />
            <div className="h-1.5 bg-muted rounded-sm w-4/5" />
            <div className="h-1.5 bg-muted rounded-sm w-3/5" />
            <div className="h-1.5 bg-muted rounded-sm w-3/4" />
            <div className="h-1.5 bg-muted rounded-sm w-1/2" />
          </div>
        </motion.div>

        {/* Arrow → */}
        <motion.div
          initial={{opacity:0, scale:0.7}} animate={{opacity:1, scale:1}} transition={{duration:0.4, delay:0.25}}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-lg"
        >
          <Sparkles className="h-6 w-6" />
        </motion.div>

        {/* Markdown output */}
        <motion.div
          initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:0.5, delay:0.45}}
          className="absolute bottom-4 right-4 w-48 bg-card border border-primary/30 rounded-sm shadow-xl p-3.5 rotate-[3deg]"
        >
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold">.md</span>
            <span className="text-[10px] font-mono text-muted-foreground">+ chunks</span>
          </div>
          <div className="mt-2 font-mono text-[10px] leading-tight space-y-1">
            <div className="text-foreground"># Resumen</div>
            <div className="text-muted-foreground">- Total: USD 1.250</div>
            <div className="text-muted-foreground">- Fecha: 14-feb-2026</div>
            <div className="text-primary">[chunk 1/12]</div>
            <div className="text-primary">[chunk 2/12]</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ChatVisual() {
  // Multi-model row with conversation bubble.
  const dots = [
    {label: "GPT", color: "bg-emerald-500"},
    {label: "C", color: "bg-amber-500"},
    {label: "G", color: "bg-blue-500"},
    {label: "OR", color: "bg-violet-500"},
  ];
  return (
    <div className="absolute inset-0 p-6 flex flex-col justify-center gap-4" data-testid="chat-visual">
      {/* Model selector row */}
      <motion.div
        initial={{opacity:0, x:-10}} animate={{opacity:1, x:0}} transition={{duration:0.4}}
        className="flex items-center gap-2"
      >
        <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Modelo</span>
        <div className="flex -space-x-1.5">
          {dots.map((d, i) => (
            <div key={i} className={`h-7 w-7 rounded-full ${d.color} border-2 border-background grid place-items-center text-[10px] font-mono font-black text-white`}>
              {d.label}
            </div>
          ))}
        </div>
      </motion.div>

      {/* User bubble */}
      <motion.div
        initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{duration:0.4, delay:0.1}}
        className="self-end max-w-[75%] bg-foreground text-background rounded-sm rounded-tr-none px-3 py-2 text-xs shadow"
      >
        ¿Cuál fue el ROI del Q4?
      </motion.div>

      {/* AI bubble */}
      <motion.div
        initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{duration:0.5, delay:0.25}}
        className="self-start max-w-[85%] bg-card border border-primary/30 rounded-sm rounded-tl-none px-3 py-2.5 text-xs shadow space-y-1"
      >
        <div className="flex items-center gap-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">faro</span>
        </div>
        <div className="font-mono text-[11px]">ROI Q4 = <span className="text-primary font-bold">+34%</span> vs Q3.</div>
        <div className="h-1 bg-primary/20 rounded-sm w-full" />
        <div className="h-1 bg-primary/20 rounded-sm w-4/5" />
        <div className="h-1 bg-primary/20 rounded-sm w-3/5" />
      </motion.div>

      {/* Composer */}
      <motion.div
        initial={{opacity:0}} animate={{opacity:1}} transition={{duration:0.4, delay:0.4}}
        className="mt-2 flex items-center gap-2 border border-border bg-card rounded-sm px-2 py-1.5"
      >
        <div className="h-1.5 bg-muted rounded-sm w-1/3" />
        <div className="ml-auto h-5 w-5 bg-primary rounded-sm grid place-items-center">
          <ArrowRight className="h-3 w-3 text-primary-foreground" />
        </div>
      </motion.div>
    </div>
  );
}

function VoiceVisual() {
  // Animated voice orb + waveform + transcript snippet.
  return (
    <div className="absolute inset-0 p-6 grid place-items-center" data-testid="voice-visual">
      <div className="relative w-full h-full grid place-items-center">
        {/* Concentric rings */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            initial={{scale:0.8, opacity:0.6}}
            animate={{scale:[0.8, 1.4, 0.8], opacity:[0.6, 0, 0.6]}}
            transition={{duration:3, repeat:Infinity, delay:i*0.7, ease:"easeInOut"}}
            className="absolute h-32 w-32 lg:h-40 lg:w-40 rounded-full border-2 border-emerald-500/40"
          />
        ))}
        {/* Center orb */}
        <motion.div
          animate={{scale:[1, 1.08, 1]}}
          transition={{duration:2.4, repeat:Infinity, ease:"easeInOut"}}
          className="relative h-24 w-24 lg:h-28 lg:w-28 rounded-full bg-gradient-to-br from-emerald-400 to-primary grid place-items-center shadow-2xl"
        >
          <Mic className="h-9 w-9 text-white" />
        </motion.div>

        {/* Transcript snippet */}
        <motion.div
          initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} transition={{duration:0.6, delay:0.3}}
          className="absolute bottom-4 left-4 right-4 bg-card border border-border rounded-sm shadow-xl p-3"
        >
          <div className="flex items-center gap-1.5">
            <Volume2 className="h-3 w-3 text-emerald-500" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">transcripción</span>
          </div>
          <div className="mt-1.5 text-[11px] font-mono">
            <span className="text-primary font-bold">[00:12]</span>{" "}
            <span className="text-foreground">…dame el resumen del informe.</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function WorkspaceVisual() {
  // Split-window mockup with sidebar, chat, and storage.
  return (
    <div className="absolute inset-0 p-5 grid place-items-center" data-testid="workspace-visual">
      <motion.div
        initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} transition={{duration:0.5}}
        className="w-full max-w-sm aspect-[4/3] bg-card border border-border rounded-sm shadow-xl overflow-hidden grid grid-cols-[28%_1fr_28%]"
      >
        {/* Sidebar */}
        <div className="bg-secondary/60 p-2 space-y-1 border-r border-border">
          <div className="h-2 bg-primary/40 rounded-sm w-full" />
          <div className="h-2 bg-muted rounded-sm w-3/4" />
          <div className="h-2 bg-muted rounded-sm w-full" />
          <div className="h-2 bg-muted rounded-sm w-2/3" />
          <div className="h-2 bg-muted rounded-sm w-3/4" />
          <div className="h-2 bg-muted rounded-sm w-1/2" />
        </div>
        {/* Chat */}
        <div className="p-2 space-y-2">
          <div className="self-end ml-auto w-3/4 h-2 bg-foreground rounded-sm" />
          <div className="w-full h-2 bg-primary/40 rounded-sm" />
          <div className="w-5/6 h-2 bg-primary/40 rounded-sm" />
          <div className="w-2/3 h-2 bg-primary/40 rounded-sm" />
          <div className="mt-3 self-end ml-auto w-1/2 h-2 bg-foreground rounded-sm" />
          <div className="w-full h-2 bg-primary/40 rounded-sm" />
        </div>
        {/* Storage */}
        <div className="bg-secondary/40 p-2 space-y-1.5 border-l border-border">
          <div className="text-[7px] font-mono uppercase tracking-wider text-muted-foreground">archivos</div>
          {[FileText, FileText, Database, Globe].map((Icon, i) => (
            <div key={i} className="flex items-center gap-1">
              <Icon className="h-2.5 w-2.5 text-muted-foreground" />
              <div className="h-1.5 bg-muted rounded-sm flex-1" />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function SecurityVisual() {
  // Encrypted vault graphic with hex/checkmark grid.
  return (
    <div className="absolute inset-0 p-6 grid place-items-center" data-testid="security-visual">
      <div className="relative w-full h-full grid place-items-center">
        {/* Background hex grid */}
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 200 200" fill="none">
          {Array.from({length: 5}).map((_, r) =>
            Array.from({length: 5}).map((_, c) => {
              const x = 20 + c * 38 + (r % 2) * 19;
              const y = 25 + r * 32;
              return (
                <polygon
                  key={`${r}-${c}`}
                  points={`${x},${y - 12} ${x + 10},${y - 6} ${x + 10},${y + 6} ${x},${y + 12} ${x - 10},${y + 6} ${x - 10},${y - 6}`}
                  stroke="currentColor"
                  className="text-rose-500"
                  strokeWidth="0.8"
                />
              );
            })
          )}
        </svg>

        {/* Center lock */}
        <motion.div
          initial={{scale:0.9, opacity:0}} animate={{scale:1, opacity:1}} transition={{duration:0.5}}
          className="relative h-28 w-28 rounded-md bg-foreground text-background grid place-items-center shadow-2xl"
        >
          <Lock className="h-12 w-12" />
        </motion.div>

        {/* Badges */}
        <motion.div
          initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{duration:0.4, delay:0.2}}
          className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-sm text-[10px] font-mono uppercase tracking-wider"
        >
          <Check className="h-3 w-3" /> AES-256
        </motion.div>
        <motion.div
          initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{duration:0.4, delay:0.3}}
          className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 px-2 py-1 border border-primary/40 bg-primary/10 text-primary rounded-sm text-[10px] font-mono uppercase tracking-wider"
        >
          <Eye className="h-3 w-3" /> Zero retention
        </motion.div>
      </div>
    </div>
  );
}
